import { useEffect, useRef, type FC, type PropsWithChildren } from 'react';
import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import { createSelector } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useTooltip } from '__components/Kizen/Tooltip';
import { KizenTypography } from '__app/typography';
import { getStepIdPreview } from '../utilities';

const HIGHLIGHT_STYLE =
  'background-color: var(--color-ai-gradient-start); color: var(--color-font-primary); font-size: inherit';

const highlightTextNode = (textNode: Text, search: string) => {
  const text = textNode.nodeValue ?? '';
  const regex = new RegExp(
    `(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'gi'
  );
  const matches = Array.from(text.matchAll(regex));
  const parent = textNode.parentNode;
  const fragment = document.createDocumentFragment();
  if (!matches.length || !parent) {
    return fragment;
  }

  let lastIndex = 0;
  matches.forEach((match) => {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    if (start > lastIndex) {
      fragment.appendChild(
        document.createTextNode(text.slice(lastIndex, start))
      );
    }
    const span = document.createElement('span');
    span.setAttribute('style', HIGHLIGHT_STYLE);
    span.textContent = match[0];
    fragment.appendChild(span);
    lastIndex = end;
  });
  if (lastIndex < text.length) {
    fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
  parent.replaceChild(fragment, textNode);
};

const highlightSearchableText = (target: HTMLElement, search: string) => {
  const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    textNodes.push(node as Text);
    node = walker.nextNode();
  }
  textNodes.forEach((textNode) => highlightTextNode(textNode, search));
};

const selectSearch = createSelector(
  (state: any) => state.search.trim().toLowerCase(),
  (state: any, stepId: string) => state.searchMatchedSteps.includes(stepId),
  (search, isMatched) => (isMatched ? search : '')
);

const SearchableInner: FC<PropsWithChildren<{ stepId: string }>> = ({
  children,
  stepId,
}) => {
  const trimmedSearch = useAutomationsSelector((state: any) =>
    selectSearch(state, stepId)
  );

  const childrenRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const lastProcessedRef = useRef<{
    search: string;
    sourceSnapshot: Node | null;
  }>({
    search: '',
    sourceSnapshot: null,
  });

  useEffect(() => {
    if (!childrenRef.current || !copyRef.current || !trimmedSearch) {
      lastProcessedRef.current = {
        search: '',
        sourceSnapshot: null,
      };
      return;
    }

    const { search, sourceSnapshot } = lastProcessedRef.current;

    if (
      search !== trimmedSearch ||
      !childrenRef.current.isEqualNode(sourceSnapshot)
    ) {
      const clone = childrenRef.current.cloneNode(true) as HTMLDivElement;
      const sourceSnapshotClone = childrenRef.current.cloneNode(true);

      highlightSearchableText(clone, trimmedSearch);

      copyRef.current.replaceChildren(...Array.from(clone.childNodes));

      lastProcessedRef.current = {
        search: trimmedSearch,
        sourceSnapshot: sourceSnapshotClone,
      };
    }
  }, [trimmedSearch, children]);

  return (
    <>
      <span
        ref={childrenRef}
        style={trimmedSearch ? { display: 'none' } : undefined}
      >
        {children}
      </span>
      {trimmedSearch ? <span ref={copyRef} /> : null}
    </>
  );
};

export const Searchable: FC<
  PropsWithChildren<{ stepId: string; enabled: boolean }>
> = ({ children, stepId, enabled = true }) =>
  enabled ? (
    <SearchableInner stepId={stepId}>{children}</SearchableInner>
  ) : (
    children
  );

export const SearchableStepId: FC<{
  stepId: string;
  enabled: boolean;
}> = ({ stepId, enabled = true }) => {
  const { t } = useTranslation();
  const trimmedSearch = useAutomationsSelector((state: any) =>
    selectSearch(state, stepId)
  );

  const [tooltipProps, tooltip] = useTooltip(
    enabled && trimmedSearch && stepId.includes(trimmedSearch)
      ? {
          html: `${t('Step ID:')} ${stepId.replaceAll(
            trimmedSearch,
            `<span style="${HIGHLIGHT_STYLE}">${trimmedSearch}</span>`
          )}`,
        }
      : {
          label: t('Step ID: {{ stepId }}', { stepId }),
        }
  );

  const stepIdPreview = getStepIdPreview(stepId);

  if (!stepIdPreview) {
    return false;
  }

  return (
    <KizenTypography {...tooltipProps} size="xs" as="span">
      <Searchable stepId={stepId} enabled={enabled}>
        {stepIdPreview}
      </Searchable>
      {tooltip}
    </KizenTypography>
  );
};
